/* eslint-disable max-lines -- this file should be split */
/* eslint-disable max-lines-per-function */
import { G } from '@mobily/ts-belt';
import Warning from '@mui/icons-material/Warning';
import { Alert, Box, Typography } from '@mui/material';
import { addHours, format } from 'date-fns';
import { Decimal } from 'decimal.js';
import { pick } from 'lodash';
import { useEffect, useMemo } from 'react';
import {
  CheckboxGroupInput,
  required,
  SelectField,
  useGetList,
  useGetOne,
  useLocaleState,
  useTranslate,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';

import { calculatePlanContributionTaxes } from '@Common/bo/calculate_plan_contribution_taxes';
import { isExemptFromCsgCrdsCampaign } from '@Common/bo/campaign_personal_assets';
import { B2B_CAMPAIGN_GRACE_PERIOD_HOURS } from '@Common/constants/b2b/campaigns';
import { FINANCIAL_INDEX_SHORT_NAME } from '@Common/constants/financial_indexes';
import {
  CampaignAllocationChoiceType,
  GetB2bCampaignOneResponse,
  PostCalculateMatchingContributionsEndpoint,
} from '@Common/types/bo/b2b/campaigns';
import { GetB2bCompanyPaymentMethodResponse } from '@Common/types/bo/b2b/companies';
import { B2bContract } from '@Common/types/bo/b2b/contracts';
import { makeIntegerAmount } from '@Common/types/currency/currency';

import { contractCampaignActionHandlerChoices } from '@sh4res/bo-common/choices/contracts';
import { CampaignTimeline } from '@sh4res/bo-common/components/B2bCampaigns/CampaignTimeline';
import { b2bCampaignOperationPaymentMethodChoices } from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { formatConsultationDate } from '@sh4res/bo-common/components/B2bCampaigns/consultationDates';
import { isCampaignVacationDaysAllocation } from '@sh4res/bo-common/components/B2bCampaigns/helpers/isCampaignVacationDaysAllocation';
import { AllocationRecord } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import { ExportPendingCampaignAllocations } from '@sh4res/bo-common/components/B2bCampaigns/steps/confirmation/components/ExportPendingCampaignAllocations';
import { getLatePaymentInterestsAmount } from '@sh4res/bo-common/components/B2bCampaigns/steps/confirmation/getLatePaymentInterestsAmount';
import { handleCampaignTransform } from '@sh4res/bo-common/components/B2bCampaigns/transform';
import { B2bCampaignFormValues } from '@sh4res/bo-common/components/B2bCampaigns/types';
import { RadioButtonGroupInput } from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { ShowSection } from '@sh4res/bo-common/components/ShowSection';
import { CurrencyField } from '@sh4res/bo-common/fields';
import {
  useCreatePromise,
  useFinancialIndexValue,
  useResourceBasePath,
  useWatchObject,
} from '@sh4res/bo-common/hooks';
import { buildCurrencyOptions, convertCentsInUnits } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bCampaignConfirmationStep';

export const ConfirmationStep = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const formValues = getValues();
  const tmoPct = useFinancialIndexValue(FINANCIAL_INDEX_SHORT_NAME.TMO);
  const [locale] = useLocaleState();
  const [getCalculatedMatchingContributions] = useCreatePromise<
    PostCalculateMatchingContributionsEndpoint['Client']['RequestBody'],
    PostCalculateMatchingContributionsEndpoint['Client']['ResponseBody']
  >();
  const resourceBasePath = useResourceBasePath();

  const {
    b2b_contract_ids: contractIds,
    amount_type_input: amountType,
    choice_collected_by: choiceCollectedBy,
    payout_by: payoutBy,
    allocation_computed_by: allocationComputedBy,
    type: campaignType,
    matching_contribution_computed_by: matchingContributionComputedBy,
    matching_contribution_related_cash_source: matchingContributionRelatedCashSource,
    value_sharing_bonus_fiscal_rule: valueSharingBonusFiscalRule,
    reason,
    fiscal_period_start: fiscalPeriodStart,
    reference_year: referenceYear,
  } = useWatchObject<B2bCampaignFormValues>()([
    'b2b_contract_ids',
    'amount_type_input',
    'choice_collected_by',
    'payout_by',
    'allocation_computed_by',
    'type',
    'matching_contribution_computed_by',
    'matching_contribution_related_cash_source',
    'value_sharing_bonus_fiscal_rule',
    'reason',
    'fiscal_period_start',
    'reference_year',
  ]);

  const firstContractId =
    Array.isArray(contractIds) && contractIds.length > 0 ? contractIds[0] : null;

  const { data: firstContract } = useGetOne<B2bContract>(
    `${resourceBasePath}contracts`,
    {
      id: firstContractId ?? 0,
    },
    { enabled: G.isNotNullable(firstContractId) },
  );

  const totalAllocatedAmount = useMemo(
    () =>
      ((formValues.allocations ?? []) as AllocationRecord[])
        .reduce((total, alloc) => total.plus(alloc.amount ?? 0), new Decimal(0))
        .toNumber(),
    [formValues],
  );

  const isCompanyVoluntaryDeposits = campaignType === 'voluntary_deposit';
  const isExemptFromCsgCrds = isExemptFromCsgCrdsCampaign(campaignType);
  const isMatchingContributionCampaign = campaignType === 'matching_contribution';
  const isCETCampaignDaysAllocation = isCampaignVacationDaysAllocation({
    type: campaignType,
    choice_collected_by: choiceCollectedBy,
  });
  const matchingContributionsEnabled =
    firstContract?.matching_contributions_enabled &&
    (!isCETCampaignDaysAllocation || matchingContributionComputedBy === 'distributor');

  const { data: calculatedMatchingContributions } = useQuery(
    [
      'totalMatchingContributions',
      formValues.company_id,
      formValues.allocations,
      reason,
      referenceYear,
      fiscalPeriodStart,
      valueSharingBonusFiscalRule,
    ],
    async (): Promise<
      PostCalculateMatchingContributionsEndpoint['Client']['ResponseBody'] & {
        allocations: PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'];
      }
    > => {
      const data = await handleCampaignTransform({ isDraft: true, action: 'create' })(formValues);
      const response = await getCalculatedMatchingContributions(
        `${resourceBasePath}campaigns/calculate_matching_contributions`,
        {
          data: {
            ...pick(data, 'company_id', 'b2b_contract_ids', 'type'),
            allocations:
              data.allocations as PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'],
            amount_type_input:
              amountType as PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['amount_type_input'],
            matching_contribution_computed_by: matchingContributionComputedBy,
            matching_contribution_related_cash_source: matchingContributionRelatedCashSource,
            reason:
              reason as PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['reason'],
            fiscal_period_start: fiscalPeriodStart,
            reference_year: referenceYear ?? null,
            value_sharing_bonus_fiscal_rule: valueSharingBonusFiscalRule,
          } satisfies PostCalculateMatchingContributionsEndpoint['Client']['RequestBody'],
        },
      );
      return {
        ...response,
        allocations:
          data.allocations as PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'],
      };
    },
    {
      enabled:
        !!formValues.company_id &&
        matchingContributionsEnabled &&
        choiceCollectedBy !== 'distributor' &&
        G.isNotNullable(matchingContributionComputedBy),
    },
  );

  const { data: formattedAllocations } = useQuery(
    ['formattedAllocations', formValues.company_id, formValues.allocations],
    async (): Promise<
      PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations']
    > => {
      const data = await handleCampaignTransform({ isDraft: true, action: 'create' })(formValues);

      return data.allocations as PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'];
    },
    {
      enabled: !!formValues.allocations?.length,
    },
  );

  const { data: paymentMethods } = useGetList<GetB2bCompanyPaymentMethodResponse[number]>(
    `${resourceBasePath}companies/payment_methods`,
    {
      filter: { b2b_company_id: formValues.company_id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'id', order: 'ASC' },
    },
  );

  const showSepaWarningMessage = useMemo<boolean>(() => {
    if (!['sepa_new', 'sepa_linked'].includes(formValues.payment_method)) {
      return false;
    }

    const sepaPaymentMethod = paymentMethods?.find((method) => method.type === 'sepa_direct_debit');
    if (sepaPaymentMethod && sepaPaymentMethod.status === 'active') {
      return false;
    }

    return true;
  }, [formValues.payment_method, paymentMethods]);

  const latePaymentInterests = useMemo(
    () =>
      getLatePaymentInterestsAmount({
        formValues: {
          is_automatic_late_payment_interests: formValues.is_automatic_late_payment_interests,
          late_payment_interests_override_amount: formValues.late_payment_interests_override_amount,
          end_date: formValues.end_date,
          fiscal_period_start: formValues.fiscal_period_start,
          fiscal_period_end: formValues.fiscal_period_end,
          type: formValues.type,
        },
        totalAllocatedAmount,
        choiceCollectedBy,
        tmoPct,
      }),
    [formValues, totalAllocatedAmount, choiceCollectedBy, tmoPct],
  );

  const amountToBePaid = useMemo(
    () =>
      formValues.allocations.reduce((acc: number, alloc: AllocationRecord) => {
        let amount = alloc.amount ?? 0;
        if (payoutBy === 'company') {
          amount -= (alloc.company_collected_choices ?? [])
            .filter((choice) => choice.allocation_type === 'payout')
            .reduce((acc, choice) => acc + (choice.amount ?? 0), 0);
        }
        return (
          acc +
          calculatePlanContributionTaxes({
            isNetAmountTaxContribution: amountType === 'net',
            amount: makeIntegerAmount(amount),
            socialStatus: alloc.social_status,
            taxResidenceCode: alloc.tax_residence_code,
            valueSharingBonusFiscalRule,
            isExemptFromCsgCrds,
          }).userCurrencyAmount
        );
      }, latePaymentInterests) as number,
    [
      formValues,
      amountType,
      latePaymentInterests,
      payoutBy,
      valueSharingBonusFiscalRule,
      isExemptFromCsgCrds,
    ],
  );

  // A matching contribution campaign without any matching contribution to pay (e.g. none of the
  // selected instruments has matching contributions enabled) must not be created.
  const hasNoMatchingContributions =
    isMatchingContributionCampaign &&
    G.isNotNullable(calculatedMatchingContributions) &&
    calculatedMatchingContributions.total_matching_contributions === 0;

  // Expose the state to the form so the stepper can disable the save button on this step.
  useEffect(() => {
    setValue('matching_contributions_total_is_zero', hasNoMatchingContributions);
  }, [hasNoMatchingContributions, setValue]);

  const amountToBeDebited =
    matchingContributionComputedBy === 'company'
      ? amountToBePaid + (calculatedMatchingContributions?.total_matching_contributions ?? 0)
      : amountToBePaid;

  const isAllocationsOnlyPayout = useMemo(
    () =>
      formValues.allocations
        .flatMap((allocation: AllocationRecord) =>
          (allocation.company_collected_choices ?? []).map((choice) => choice.allocation_type),
        )
        .every(
          (allocationType: CampaignAllocationChoiceType | null | undefined) =>
            allocationType === 'payout',
        ) as boolean,
    [formValues],
  );

  const date = addHours(formValues.end_date, B2B_CAMPAIGN_GRACE_PERIOD_HOURS);
  const allocatorDependant =
    choiceCollectedBy === 'distributor'
      ? {
          endDate: formValues.end_date,
          startDate: formValues.start_date,
          fundRequestMessage: translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.funds_request_date_value',
            {
              date: `${format(date, `dd/MM/yyyy '${translate('resources.bo_common.b2b.campaigns.sections.confirmation.funds_request_date_value_at')}' HH:mm:ss`)} (${format(date, 'O')})`,
              hours: B2B_CAMPAIGN_GRACE_PERIOD_HOURS,
            },
          ),
          confirmSepaDirectMessage: translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.confirm_sepa_debit',
          ),
          amountToBePaidMessage: translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.total_amount_to_be_value',
          ),
          matchingContributionsToBePaidMessage: translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.matching_contributions_to_be_value',
          ),
        }
      : {
          endDate: new Date(),
          startDate: new Date(),
          fundRequestMessage: isAllocationsOnlyPayout
            ? undefined
            : format(new Date(), 'dd/MM/yyyy'),
          confirmSepaDirectMessage: translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.confirm_sepa_debit_company_allocator',
            {
              total_amount_to_be_paid: new Intl.NumberFormat(
                locale,
                buildCurrencyOptions('EUR', 2),
              ).format(convertCentsInUnits(amountToBeDebited)),
            },
          ),
          amountToBePaidMessage: (
            <CurrencyField
              data-testid={`${testId}_AmountToBePaidField`}
              source="amount"
              currency="eur"
              record={{
                amount: amountToBePaid,
              }}
            />
          ),
          matchingContributionsToBePaidMessage: (
            <CurrencyField
              data-testid={`${testId}_MatchingContributionsToBePaidField`}
              source="amount"
              currency="eur"
              record={{
                amount: calculatedMatchingContributions?.total_matching_contributions ?? null,
              }}
            />
          ),
        };
  return (
    <Box>
      <Typography variant="h6" fontWeight="bold">
        {translate('resources.bo_common.b2b.campaigns.sections.confirmation.summary')}
      </Typography>

      <Typography>
        {translate('resources.bo_common.b2b.campaigns.sections.confirmation.operation_type')}:{' '}
        {translate(
          `resources.bo_common.b2b.campaigns.choices.type.${formValues.type as GetB2bCampaignOneResponse['type']}`,
        )}
      </Typography>

      {G.isNotNullable(formValues.fiscal_period_start) &&
        G.isNotNullable(formValues.fiscal_period_end) && (
          <Typography>
            {translate(
              'resources.bo_common.b2b.campaigns.sections.confirmation.reference_fiscal_period',
            )}
            :{' '}
            {translate(
              'resources.bo_common.b2b.campaigns.sections.confirmation.reference_fiscal_period_value',
              {
                from: format(formValues.fiscal_period_start, 'dd/MM/yyyy'),
                to: format(formValues.fiscal_period_end, 'dd/MM/yyyy'),
              },
            )}
          </Typography>
        )}

      {G.isNotNullable(referenceYear) && (
        <Typography>
          {translate('resources.bo_common.b2b.campaigns.sections.confirmation.reference_year')}:{' '}
          {referenceYear}
        </Typography>
      )}

      {!isCompanyVoluntaryDeposits && !isMatchingContributionCampaign && (
        <Typography>
          {translate(
            `resources.bo_common.b2b.campaigns.sections.confirmation.${amountType === 'gross' ? 'gross_amount' : 'net_amount'}`,
          )}
          :
          <CurrencyField
            data-testid={`${testId}_TotalAllocatedAmountField`}
            source="amount"
            currency="eur"
            record={{
              amount: totalAllocatedAmount,
            }}
          />
        </Typography>
      )}

      {!isMatchingContributionCampaign && (
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.total_amount_to_be_paid',
          )}
          :
          {isCETCampaignDaysAllocation ? (
            <CurrencyField
              data-testid={`${testId}_CETAmountToBePaidField`}
              source="amount"
              currency="eur"
              record={{
                amount: amountToBePaid,
              }}
            />
          ) : (
            allocatorDependant.amountToBePaidMessage
          )}
        </Typography>
      )}

      {isCompanyVoluntaryDeposits && (
        <Typography>
          {translate(
            formValues.payment_method === 'sepa'
              ? 'resources.bo_common.b2b.campaigns.sections.confirmation.sepa_direct_debit_date'
              : 'resources.bo_common.b2b.campaigns.sections.confirmation.funds_request_date',
          )}
          : {allocatorDependant.fundRequestMessage}
        </Typography>
      )}

      {matchingContributionsEnabled && G.isNotNullable(matchingContributionComputedBy) && (
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.matching_contributions_to_be_paid',
          )}
          : {allocatorDependant.matchingContributionsToBePaidMessage}
          <br />
          {isCompanyVoluntaryDeposits &&
            translate(
              'resources.bo_common.b2b.campaigns.sections.confirmation.matching_contributions_next_fund_request',
            )}
        </Typography>
      )}

      {hasNoMatchingContributions && (
        <Alert
          data-testid={`${testId}_NoMatchingContributionsAlert`}
          severity="error"
          variant="filled"
          sx={{ my: 2 }}>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.matching_contributions_zero',
          )}
        </Alert>
      )}

      {choiceCollectedBy === 'distributor' && (
        <Typography>
          {translate('resources.bo_common.b2b.campaigns.sections.confirmation.consultation_period')}
          :{' '}
          {translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.consultation_period_value',
            {
              from: formatConsultationDate(formValues.start_date),
              to: formatConsultationDate(formValues.end_date),
            },
          )}
        </Typography>
      )}

      {!isCompanyVoluntaryDeposits &&
        !isCETCampaignDaysAllocation &&
        G.isNotNullable(allocatorDependant.fundRequestMessage) && (
          <Typography>
            {translate(
              formValues.payment_method === 'sepa'
                ? 'resources.bo_common.b2b.campaigns.sections.confirmation.sepa_direct_debit_date'
                : 'resources.bo_common.b2b.campaigns.sections.confirmation.funds_request_date',
            )}
            : {allocatorDependant.fundRequestMessage}
          </Typography>
        )}

      {latePaymentInterests > 0 && campaignType !== 'unused_vacation_days' && (
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.confirmation.late_payment_interests',
          )}
          :{' '}
          <CurrencyField
            data-testid={`${testId}_LatePaymentInterestsField`}
            source="late_payment_interests"
            currency="eur"
            record={{
              late_payment_interests: latePaymentInterests,
            }}
          />
        </Typography>
      )}

      <Typography>
        {translate('resources.bo_common.b2b.campaigns.sections.confirmation.employees_concerned')}:{' '}
        {
          formValues.allocations.filter(
            (alloc: AllocationRecord) => (alloc.amount ?? 0) > 0 || isMatchingContributionCampaign,
          ).length
        }
      </Typography>

      <ExportPendingCampaignAllocations
        matchingContributions={calculatedMatchingContributions?.matching_contributions}
        allocations={formattedAllocations}
      />

      {!isCompanyVoluntaryDeposits && !isMatchingContributionCampaign && (
        <ShowSection
          noCard
          leftAligned
          label="resources.bo_common.b2b.campaigns.sections.general.operation_processing">
          <Typography>
            {translate(
              'resources.bo_common.b2b.campaigns.sections.confirmation.choice_collected_by',
            )}
            :{' '}
            <SelectField
              data-testid={`${testId}_ChoiceCollectedByField`}
              choices={contractCampaignActionHandlerChoices}
              source="value"
              record={{ value: choiceCollectedBy }}
            />
          </Typography>
          <Typography>
            {translate('resources.bo_common.b2b.campaigns.sections.confirmation.payout_by')}:{' '}
            <SelectField
              data-testid={`${testId}_PayoutByField`}
              choices={contractCampaignActionHandlerChoices}
              source="value"
              record={{ value: payoutBy }}
            />
          </Typography>
          <Typography>
            {translate(
              'resources.bo_common.b2b.campaigns.sections.confirmation.allocation_computed_by',
            )}
            :{' '}
            <SelectField
              data-testid={`${testId}_AllocationComputedByField`}
              choices={contractCampaignActionHandlerChoices}
              source="value"
              record={{ value: allocationComputedBy }}
            />
          </Typography>
          {matchingContributionComputedBy !== null && (
            <Typography>
              {translate(
                'resources.bo_common.b2b.campaigns.sections.confirmation.matching_contribution_computed_by',
              )}
              :{' '}
              <SelectField
                data-testid={`${testId}_MatchingContributionComputedByField`}
                choices={contractCampaignActionHandlerChoices}
                source="value"
                record={{ value: matchingContributionComputedBy }}
              />
            </Typography>
          )}
        </ShowSection>
      )}

      {choiceCollectedBy === 'distributor' && (
        <CampaignTimeline
          record={{
            start_date: formValues.start_date,
            end_date: formValues.end_date,
            is_emails_enabled: formValues.is_emails_enabled,
          }}
        />
      )}

      <Typography variant="h6" fontWeight="bold" mt={2}>
        {translate('resources.bo_common.b2b.campaigns.sections.confirmation.payment_method_title')}
      </Typography>

      {showSepaWarningMessage && (
        <Typography sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Warning />
          <span>
            {translate('resources.bo_common.b2b.campaigns.sections.confirmation.sepa_warning')}
          </span>
        </Typography>
      )}

      <RadioButtonGroupInput
        data-testid={`${testId}_PaymentMethodInput`}
        source="payment_method"
        choices={b2bCampaignOperationPaymentMethodChoices}
        disabled
      />

      <Typography variant="h6" fontWeight="bold" mt={2}>
        {translate('resources.bo_common.b2b.campaigns.sections.confirmation.confirmations_title')}
      </Typography>

      <CheckboxGroupInput
        data-testid={`${testId}_CashSourcesBracketInput`}
        source="cash_sources_bracket"
        label={false}
        isRequired
        validate={[required()]}
        choices={[
          {
            id: 'si',
            name: 'resources.bo_common.b2b.campaigns.sections.confirmation.confirm_accuracy',
          },
        ]}
      />

      {['sepa_new', 'sepa_linked'].includes(formValues.payment_method) && (
        <CheckboxGroupInput
          data-testid={`${testId}_ConfirmSepaDebitInput`}
          source="confirm_sepa_debit"
          label={false}
          isRequired
          validate={[required()]}
          choices={[
            {
              id: 'si',
              name: allocatorDependant.confirmSepaDirectMessage,
            },
          ]}
        />
      )}
    </Box>
  );
};
